import { FormGroup } from '@angular/forms';

import type { DropdownData, FilteredValue, RepeatableSubField, RepeatableSubFieldContext } from '../../../interfaces/input.interface';
import { RepeatableValueUtils } from './repeatable-value.utils';
import type { RepeatableLogger, RepeatableRowValue } from './repeatable-group.types';

export class RepeatableDropdownResolver {
    public constructor(
        private readonly logger: RepeatableLogger,
        private readonly getRowsSnapshotAsStrings: () => Array<Record<string, string>>,
        private readonly getRowSnapshot: (rowIndex: number) => RepeatableRowValue,
        private readonly getModel: () => FormGroup | null,
    ) {}

    public options(subField: RepeatableSubField, rowIndex: number): Array<FilteredValue<unknown>> {
        try {
            const dropdownData = this.getDropdownData(subField, rowIndex);
            const configuration = subField.input?.dropdownConfiguration;
            const filteredValues = (dropdownData?.filtered ?? []) as Array<FilteredValue<unknown>>;
            const values = (dropdownData?.values ?? []) as Array<FilteredValue<unknown>>;
            const hasFilteredValues = filteredValues.some((item) => item?.value !== null && item?.value !== undefined);
            const hasValues = values.some((item) => item?.value !== null && item?.value !== undefined);
            let baseValues: Array<FilteredValue<unknown>> = [];

            if (configuration?.filteredValues?.length) {
                baseValues = configuration.filteredValues as Array<FilteredValue<unknown>>;
            } else if (hasValues) {
                baseValues = values;
            } else if (hasFilteredValues) {
                baseValues = filteredValues;
            }

            const filter = configuration?.filter as ((it: FilteredValue<unknown>) => boolean | undefined) | undefined;

            if (!filter) {
                return baseValues;
            }

            return baseValues.filter((item) => {
                try {
                    return filter(item) !== false;
                } catch (error: unknown) {
                    this.logger.warn('dropdownOptions(): filter threw, keeping item by default.', {
                        key: subField.key,
                        rowIndex,
                        error,
                    });

                    return true;
                }
            });
        } catch (error: unknown) {
            this.logger.warn('dropdownOptions(): unexpected error, returning empty list.', {
                key: subField.key,
                rowIndex,
                error,
            });

            return [];
        }
    }

    public mapIdToDropdownValue(idRaw: unknown, subField: RepeatableSubField, rowIndex: number): unknown | null {
        const id = String(idRaw ?? '').trim();

        if (id === '' || RepeatableValueUtils.isNullishStringToken(id)) {
            return null;
        }

        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const options = this.options(subField, rowIndex);
        const found = options.find((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === id);

        if (found) {
            return found.value;
        }

        const foundByNormalizedToken = this.mapByNormalizedOptionToken(id, options);

        if (foundByNormalizedToken !== null) {
            this.logger.log('mapIdToDropdownValue(): resolved by normalized token.', {
                key: subField.key,
                rowIndex,
                id,
            });

            return foundByNormalizedToken;
        }

        return this.mapByFallbackLabel(subField, rowIndex, id, primaryKey, options);
    }

    public idsToLabels(subField: RepeatableSubField, ids: string[], rowIndex: number): string[] {
        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const options = this.options(subField, rowIndex);

        return ids
            .map((id) => options.find((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === id)?.label ?? '')
            .filter((label) => label.trim() !== '');
    }

    public logWhySelectCannotRender(subField: RepeatableSubField, rowIndex: number, rawId: string): void {
        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const options = this.options(subField, rowIndex);
        const dropdownData = this.getDropdownData(subField, rowIndex);
        const baseValuesLength = Array.isArray(dropdownData?.values) ? dropdownData.values.length : 0;
        const baseFilteredLength = Array.isArray(dropdownData?.filtered) ? dropdownData.filtered.length : 0;
        const reason = options.length === 0
            ? 'Options list is empty.'
            : options.some((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === rawId)
                ? 'ID exists in options but UI still blank.'
                : 'ID not found in options.';

        this.logger.warn('Select cannot display value.', {
            rowIndex,
            key: subField.key,
            rawId,
            primaryKey,
            optionsLen: options.length,
            baseValuesLength,
            baseFilteredLength,
            reason,
        });
    }

    public getDropdownData(subField: RepeatableSubField, rowIndex: number): DropdownData<unknown> | undefined {
        if (typeof subField.getData === 'function') {
            try {
                return subField.getData(this.buildSubFieldContext(rowIndex));
            } catch (error: unknown) {
                this.logger.warn('getData() threw, falling back to static data.', {
                    key: subField.key,
                    rowIndex,
                    error,
                });
            }
        }

        return subField.input?.data as DropdownData<unknown> | undefined;
    }

    private mapByNormalizedOptionToken(id: string, options: Array<FilteredValue<unknown>>): unknown | null {
        const normalizedId = this.normalizeLooseDropdownToken(id);

        if (normalizedId === '') {
            return null;
        }

        const found = options.find((option) => {
            const normalizedLabel = this.normalizeLooseDropdownToken(option?.label);
            const normalizedValue = this.normalizeLooseDropdownToken(option?.value);

            return normalizedLabel === normalizedId || normalizedValue === normalizedId;
        });

        return found?.value ?? null;
    }

    private normalizeLooseDropdownToken(value: unknown): string {
        if (value === null || value === undefined || typeof value === 'object') {
            return '';
        }

        return RepeatableValueUtils.normalizeOptionLabel(value).replace(/\s+/g, '');
    }

    private mapByFallbackLabel(
        subField: RepeatableSubField,
        rowIndex: number,
        id: string,
        primaryKey: string,
        options: Array<FilteredValue<unknown>>,
    ): unknown | null {
        const fallbackLabel = this.getFallbackRowLabel(subField, rowIndex);

        if (fallbackLabel === '') {
            return null;
        }

        const normalizedFallbackLabel = RepeatableValueUtils.normalizeOptionLabel(fallbackLabel);
        const foundByLabel = options.find((option) => {
            const optionLabel = RepeatableValueUtils.normalizeOptionLabel(option?.label ?? '');

            return optionLabel !== '' && optionLabel === normalizedFallbackLabel;
        });

        if (foundByLabel) {
            this.logger.log('mapIdToDropdownValue(): resolved by fallback label.', {
                key: subField.key,
                rowIndex,
                id,
                fallbackLabel,
                resolvedId: RepeatableValueUtils.getOptionKey(foundByLabel.value, primaryKey),
            });

            return foundByLabel.value;
        }

        return null;
    }

    private buildSubFieldContext(rowIndex: number): RepeatableSubFieldContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.getModel() ?? new FormGroup({}),
        };
    }

    private getFallbackRowLabel(subField: RepeatableSubField, rowIndex: number): string {
        const row = this.getRowSnapshot(rowIndex);
        const labelControlKey = this.getFallbackLabelControlKey(subField);

        if (!labelControlKey) {
            return '';
        }

        return String(row[labelControlKey] ?? '').trim();
    }

    private getFallbackLabelControlKey(subField: RepeatableSubField): string | null {
        switch (subField.key) {
            case 'uso':
                return 'usoNombre';

            case 'agente':
                return 'agenteNombre';

            default:
                return null;
        }
    }
}
